import { Field, TableName, FullColumnName } from '@models/column';

export type Aggregation =
  | AbsoluteTemporalChangeCountRecords
  | AbsoluteTemporalChangeMax
  | AbsoluteTemporalChangeMean
  | AbsoluteTemporalChangeMin
  | AbsoluteTemporalChangeSum
  | AnyBetween
  | AnyValue
  | AsIs
  | C3
  | CWT
  | ClosestTimeDifference
  | Count
  | CountBetween
  | CountOfValue
  | CountRecords
  | CountUnique
  | DayOfWeek
  | Distance
  | Exist
  | GridTargetMeanEncode
  | HistogramEqualityRatio
  | Hour
  | LatestKMax
  | LatestKMaxMinusMin
  | LatestKMean
  | LatestKMin
  | LatestKStd
  | LatestKSum
  | LatestTimeDifference
  | Max
  | MaxDistance
  | MaxInterval
  | Mean
  | MeanDistance
  | MeanInterval
  | MeanTimeDifference
  | Min
  | MinDistance
  | MinInterval
  | Month
  | N1RedundantMean
  | N1RedundantNullRatio
  | N1RedundantRatioOfValue
  | N1RedundantSum
  | N1RedundantTopicWeightMean
  | N1RedundantTopicWeightSum
  | NgramCount
  | NonBusinessDays
  | NullRatio
  | RatioBetween
  | RatioOfValue
  | Std
  | StdDistance
  | StdInterval
  | StdTimeDifference
  | Sum
  | SumDistance
  | TargetMeanEncode
  | TargetTimeDifference
  | TemporalChangeCountRecords
  | TemporalChangeMax
  | TemporalChangeMean
  | TemporalChangeMin
  | TemporalChangeRatioCountRecords
  | TemporalChangeRatioMax
  | TemporalChangeRatioMean
  | TemporalChangeRatioMin
  | TemporalChangeRatioSum
  | TemporalChangeSum
  | TimeDifference
  | TopicWeightMax
  | TopicWeightMean
  | TopicWeightMin
  | TopicWeightSum
  | TopicWeightValue;

export namespace Aggregation {
  export enum Type {
    AbsoluteTemporalChangeCountRecords = 'AbsoluteTemporalChangeCountRecords',
    AbsoluteTemporalChangeMax = 'AbsoluteTemporalChangeMax',
    AbsoluteTemporalChangeMean = 'AbsoluteTemporalChangeMean',
    AbsoluteTemporalChangeMin = 'AbsoluteTemporalChangeMin',
    AbsoluteTemporalChangeSum = 'AbsoluteTemporalChangeSum',
    AnyBetween = 'AnyBetween', // HistogramFlag
    AnyValue = 'AnyValue', // HistogramEqualityFlag
    AsIs = 'AsIs',
    C3 = 'C3',
    CWT = 'CWT',
    ClosestTimeDifference = 'ClosestTimeDifference',
    Count = 'Count',
    CountBetween = 'CountBetween', // HistogramCount
    CountOfValue = 'CountOfValue', // HistogramEqualityCount
    CountRecords = 'CountRecords',
    CountUnique = 'CountUnique',
    DayOfWeek = 'DayOfWeek',
    Distance = 'Distance',
    Exist = 'Exist',
    GridTargetMeanEncode = 'GridTargetMeanEncode',
    HistogramEqualityRatio = 'HistogramEqualityRatio',
    Hour = 'Hour',
    LatestKMax = 'LatestKMax',
    LatestKMaxMinusMin = 'LatestKMaxMinusMin',
    LatestKMean = 'LatestKMean',
    LatestKMin = 'LatestKMin',
    LatestKStd = 'LatestKStd',
    LatestKSum = 'LatestKSum',
    LatestTimeDifference = 'LatestTimeDifference',
    Max = 'Max',
    MaxDistance = 'MaxDistance',
    MaxInterval = 'MaxInterval',
    Mean = 'Mean',
    MeanDistance = 'MeanDistance',
    MeanInterval = 'MeanInterval',
    MeanTimeDifference = 'MeanTimeDifference',
    Min = 'Min',
    MinDistance = 'MinDistance',
    MinInterval = 'MinInterval',
    Month = 'Month',
    N1RedundantMean = 'N1RedundantMean',
    N1RedundantNullRatio = 'N1RedundantNullRatio',
    N1RedundantRatioOfValue = 'N1RedundantRatioOfValue',
    N1RedundantSum = 'N1RedundantSum',
    N1RedundantTopicWeightMean = 'N1RedundantTopicWeightMean',
    N1RedundantTopicWeightSum = 'N1RedundantTopicWeightSum',
    NgramCount = 'NgramCount',
    NonBusinessDays = 'NonBusinessDays',
    NullRatio = 'NullRatio',
    RatioBetween = 'RatioBetween', // HistogramRatio
    RatioOfValue = 'RatioOfValue',
    Std = 'Std',
    StdDistance = 'StdDistance',
    StdInterval = 'StdInterval',
    StdTimeDifference = 'StdTimeDifference',
    Sum = 'Sum',
    SumDistance = 'SumDistance',
    TargetMeanEncode = 'TargetMeanEncode',
    TargetTimeDifference = 'TargetTimeDifference',
    TemporalChangeCountRecords = 'TemporalChangeCountRecords',
    TemporalChangeMax = 'TemporalChangeMax',
    TemporalChangeMean = 'TemporalChangeMean',
    TemporalChangeMin = 'TemporalChangeMin',
    TemporalChangeRatioCountRecords = 'TemporalChangeRatioCountRecords',
    TemporalChangeRatioMax = 'TemporalChangeRatioMax',
    TemporalChangeRatioMean = 'TemporalChangeRatioMean',
    TemporalChangeRatioMin = 'TemporalChangeRatioMin',
    TemporalChangeRatioSum = 'TemporalChangeRatioSum',
    TemporalChangeSum = 'TemporalChangeSum',
    TimeDifference = 'TimeDifference',
    TopicWeightMax = 'TopicWeightMax',
    TopicWeightMean = 'TopicWeightMean',
    TopicWeightMin = 'TopicWeightMin',
    TopicWeightSum = 'TopicWeightSum',
    TopicWeightValue = 'TopicWeightValue',
  }
}

interface TemporalChange<T> {
  type: T;
  source_time_column: FullColumnName;
  target_time_column: FullColumnName;
  source_column: FullColumnName;
  upper_bound: string;
  lower_bound: string;
  range_shift: string;
}

type TemporalChangeCount<T> = Omit<TemporalChange<T>, 'source_column'>;

interface LatestK<T> {
  type: T;
  source_time_filed: FullColumnName;
  source_field: FullColumnName;
  k: number;
}

interface N1Redundant<T> {
  type: T;
  field: FullColumnName;
}

export interface TopicWeight<T> {
  type: T;
  text_topic_field: FullColumnName;
  original_text_column_name: FullColumnName;
  index: number;
}

/***************************************************************************/

export type AbsoluteTemporalChangeCountRecords =
  TemporalChangeCount<Aggregation.Type.AbsoluteTemporalChangeCountRecords>;
export type AbsoluteTemporalChangeMax =
  TemporalChange<Aggregation.Type.AbsoluteTemporalChangeMax>;
export type AbsoluteTemporalChangeMean =
  TemporalChange<Aggregation.Type.AbsoluteTemporalChangeMean>;
export type AbsoluteTemporalChangeMin =
  TemporalChange<Aggregation.Type.AbsoluteTemporalChangeMin>;
export type AbsoluteTemporalChangeSum =
  TemporalChange<Aggregation.Type.AbsoluteTemporalChangeSum>;

export interface AnyBetween {
  type: Aggregation.Type.AnyBetween;
  field: Field;
  lower_edge: number;
  upper_edge: number;
}

export interface AnyValue {
  type: Aggregation.Type.AnyValue;
  field: Field;
  value: number;
}

export interface AsIs {
  type: Aggregation.Type.AsIs;
  column: FullColumnName;
}

export interface C3 {
  type: Aggregation.Type.C3;
  source_time_field: Field;
  source_field: Field;
  lag: number;
}

export interface CWT {
  type: Aggregation.Type.CWT;
  source_time_field: Field;
  source_field: Field;
  index: number;
  width: number;
}

export interface ClosestTimeDifference {
  type: Aggregation.Type.ClosestTimeDifference;
  source_field: Field;
  target_field: Field;
  shift: string;
}

export interface Count {
  type: Aggregation.Type.Count;
  field: Field;
}

export interface CountBetween {
  type: Aggregation.Type.CountBetween;
  field: Field;
  lower_edge: number;
  upper_edge: number;
}

export interface CountOfValue {
  type: Aggregation.Type.CountOfValue;
  field: FullColumnName;
  value: number;
}

export interface CountRecords {
  type: Aggregation.Type.CountRecords;
  table: TableName;
}

export interface CountUnique {
  type: Aggregation.Type.CountUnique;
  column: FullColumnName;
}

export interface DayOfWeek {
  type: Aggregation.Type.DayOfWeek;
  column: FullColumnName;
}

export interface Distance {
  type: Aggregation.Type.Distance;
  latitutde_1_column: FullColumnName;
  latitutde_2_column: FullColumnName;
  longitude_1_column: FullColumnName;
  longitude_2_column: FullColumnName;
}

export interface Exist {
  type: Aggregation.Type.Exist;
  table: TableName;
}

export interface GridTargetMeanEncode {
  type: Aggregation.Type.GridTargetMeanEncode;
  latitude_field: Field;
  longitude_field: Field;
  encoder_id: number;
  distance: string;
}

export interface HistogramEqualityRatio {
  type: Aggregation.Type.HistogramEqualityRatio;
  field: FullColumnName;
  value: number;
}

export interface Hour {
  type: Aggregation.Type.Hour;
  column: FullColumnName;
}

export type LatestKMax = LatestK<Aggregation.Type.LatestKMax>;
export type LatestKMaxMinusMin = LatestK<Aggregation.Type.LatestKMaxMinusMin>;
export type LatestKMean = LatestK<Aggregation.Type.LatestKMean>;
export type LatestKMin = LatestK<Aggregation.Type.LatestKMin>;
export type LatestKStd = LatestK<Aggregation.Type.LatestKStd>;
export type LatestKSum = LatestK<Aggregation.Type.LatestKSum>;

export interface LatestTimeDifference {
  type: Aggregation.Type.LatestTimeDifference;
  source_field: FullColumnName;
  target_field: FullColumnName;
  shift: string;
}

export interface Max {
  type: Aggregation.Type.Max;
  column: FullColumnName;
}

export interface MaxDistance {
  type: Aggregation.Type.MaxDistance;
  latitutde_1_column: FullColumnName;
  latitutde_2_column: FullColumnName;
  longitude_1_column: FullColumnName;
  longitude_2_column: FullColumnName;
}

export interface MaxInterval {
  type: Aggregation.Type.MaxInterval;
  column: FullColumnName;
}

export interface Mean {
  type: Aggregation.Type.Mean;
  field: FullColumnName;
}

export interface MeanDistance {
  type: Aggregation.Type.MeanDistance;
  latitutde_1_column: FullColumnName;
  latitutde_2_column: FullColumnName;
  longitude_1_column: FullColumnName;
  longitude_2_column: FullColumnName;
}

export interface MeanInterval {
  type: Aggregation.Type.MeanInterval;
  column: FullColumnName;
}

export interface MeanTimeDifference {
  type: Aggregation.Type.MeanTimeDifference;
  column1: FullColumnName;
  column2: FullColumnName;
  unit: string;
}

export interface Min {
  type: Aggregation.Type.Min;
  column: FullColumnName;
}

export interface MinDistance {
  type: Aggregation.Type.MinDistance;
  latitutde_1_column: FullColumnName;
  latitutde_2_column: FullColumnName;
  longitude_1_column: FullColumnName;
  longitude_2_column: FullColumnName;
}

export interface MinInterval {
  type: Aggregation.Type.MinInterval;
  column: FullColumnName;
}

export interface Month {
  type: Aggregation.Type.Month;
  column: FullColumnName;
}

export type N1RedundantMean = N1Redundant<Aggregation.Type.N1RedundantMean>;
export type N1RedundantNullRatio =
  N1Redundant<Aggregation.Type.N1RedundantNullRatio>;

export interface N1RedundantRatioOfValue
  extends N1Redundant<Aggregation.Type.N1RedundantRatioOfValue> {
  ratio_value: number | string | boolean;
  null_inclusion: boolean;
}

export type N1RedundantSum = N1Redundant<Aggregation.Type.N1RedundantSum>;

export interface N1RedundantTopicWeightMean {
  type: Aggregation.Type.N1RedundantTopicWeightMean;
  text_topic_field: FullColumnName;
  original_text_column_name: FullColumnName;
  index: number;
}

export interface N1RedundantTopicWeightSum {
  type: Aggregation.Type.N1RedundantTopicWeightSum;
  text_topic_field: FullColumnName;
  original_text_column_name: FullColumnName;
  index: number;
}

export interface NgramCount {
  type: Aggregation.Type.NgramCount;
  field: FullColumnName;
  ngram: string[];
}

export interface NonBusinessDays {
  type: Aggregation.Type.NonBusinessDays;
  column: FullColumnName;
}

export interface NullRatio {
  type: Aggregation.Type.NullRatio;
  field: Field;
}

export interface RatioBetween {
  type: Aggregation.Type.RatioBetween;
  field: FullColumnName;
  lower_edge: number;
  upper_edge: number;
}

export interface RatioOfValue {
  type: Aggregation.Type.RatioOfValue;
  field: Field;
  ratio_value: number | string | boolean;
  null_inclusion: boolean;
}

export interface Std {
  type: Aggregation.Type.Std;
  field: Field;
}

export interface StdDistance {
  type: Aggregation.Type.StdDistance;
  latitutde_1_column: FullColumnName;
  latitutde_2_column: FullColumnName;
  longitude_1_column: FullColumnName;
  longitude_2_column: FullColumnName;
}

export interface StdInterval {
  type: Aggregation.Type.StdInterval;
  column: FullColumnName;
}

export interface StdTimeDifference {
  type: Aggregation.Type.StdTimeDifference;
  column1: FullColumnName;
  column2: FullColumnName;
  unit: string;
}

export interface Sum {
  type: Aggregation.Type.Sum;
  column: FullColumnName;
}

export interface SumDistance {
  type: Aggregation.Type.SumDistance;
  latitutde_1_column: FullColumnName;
  latitutde_2_column: FullColumnName;
  longitude_1_column: FullColumnName;
  longitude_2_column: FullColumnName;
}

export interface TargetMeanEncode {
  type: Aggregation.Type.TargetMeanEncode;
  field: Field;
  encoder_id: number;
}

export interface TargetTimeDifference {
  type: Aggregation.Type.TargetTimeDifference;
  source_field: FullColumnName;
  target_field: FullColumnName;
  shift: string;
}

export type TemporalChangeCountRecords =
  TemporalChangeCount<Aggregation.Type.TemporalChangeCountRecords>;
export type TemporalChangeMax =
  TemporalChange<Aggregation.Type.TemporalChangeMax>;
export type TemporalChangeMean =
  TemporalChange<Aggregation.Type.TemporalChangeMean>;
export type TemporalChangeMin =
  TemporalChange<Aggregation.Type.TemporalChangeMin>;
export type TemporalChangeSum =
  TemporalChange<Aggregation.Type.TemporalChangeSum>;

export type TemporalChangeRatioCountRecords =
  TemporalChangeCount<Aggregation.Type.TemporalChangeRatioCountRecords>;
export type TemporalChangeRatioMax =
  TemporalChange<Aggregation.Type.TemporalChangeRatioMax>;
export type TemporalChangeRatioMean =
  TemporalChange<Aggregation.Type.TemporalChangeRatioMean>;
export type TemporalChangeRatioMin =
  TemporalChange<Aggregation.Type.TemporalChangeRatioMin>;
export type TemporalChangeRatioSum =
  TemporalChange<Aggregation.Type.TemporalChangeRatioSum>;

export interface TimeDifference {
  type: Aggregation.Type.TimeDifference;
  column1: FullColumnName;
  column2: FullColumnName;
  unit: string;
}

export type TopicWeightMax = TopicWeight<Aggregation.Type.TopicWeightMax>;
export type TopicWeightMean = TopicWeight<Aggregation.Type.TopicWeightMean>;
export type TopicWeightMin = TopicWeight<Aggregation.Type.TopicWeightMin>;
export type TopicWeightSum = TopicWeight<Aggregation.Type.TopicWeightSum>;
export type TopicWeightValue = TopicWeight<Aggregation.Type.TopicWeightValue>;
